<template>
  <div class="container-fluid">
    <!-- Planner Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="modern-card p-4" style="background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <h2 class="mb-1 fw-bold text-white">LifeSkills Planner</h2>
              <p class="mb-0 text-white opacity-75">Plan your day and track your progress</p>
            </div>
            <div class="text-white" style="font-size: 48px;">
              📅
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- View Toggle -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="d-flex justify-content-center gap-0">
          <button 
            v-for="view in views" 
            :key="view"
            class="btn view-btn"
            :class="{ active: selectedView === view }"
            @click="switchView(view)"
          >
            {{ view.toUpperCase() }}
          </button>
        </div>
      </div>
    </div>

    <div class="row">
      <!-- Calendar Section -->
      <div class="col-lg-8 mb-4">
        <div class="modern-card p-4">
          <!-- Calendar Header -->
          <div class="d-flex justify-content-between align-items-center mb-4">
            <button class="btn btn-outline-primary" @click="previousPeriod">
              <i class="bi bi-chevron-left"></i>
            </button>
            <h4 class="fw-bold mb-0">{{ currentPeriodTitle }}</h4>
            <button class="btn btn-outline-primary" @click="nextPeriod">
              <i class="bi bi-chevron-right"></i>
            </button>
          </div>

          <!-- Month View -->
          <div v-if="selectedView === 'month'" class="calendar-grid">
            <!-- Day Headers -->
            <div v-for="day in dayHeaders" :key="day" class="calendar-header">
              {{ day }}
            </div>
            
            <!-- Calendar Days -->
            <div 
              v-for="date in calendarDates" 
              :key="date.key"
              class="calendar-day"
              :class="{
                'other-month': !date.isCurrentMonth,
                'today': date.isToday,
                'selected': date.isSelected,
                'has-tasks': date.hasTasks
              }"
              @click="selectDate(date)"
            >
              <span class="date-number">{{ date.day }}</span>
              <div v-if="date.hasTasks" class="task-indicator"></div>
              <div v-if="date.taskCount > 0" class="task-count">{{ date.taskCount }}</div>
            </div>
          </div>

          <!-- Week View -->
          <div v-else-if="selectedView === 'week'" class="week-view">
            <div class="week-header">
              <div v-for="day in weekDays" :key="day.key" class="week-day-header">
                <div class="day-name">{{ day.name }}</div>
                <div class="day-date" :class="{ 'today': day.isToday }">{{ day.date }}</div>
              </div>
            </div>
            <div class="week-body">
              <div v-for="day in weekDays" :key="day.key" class="week-day-column" @click="selectDate(day)">
                <div v-for="task in getTasksForDate(day.fullDate)" :key="task.id" class="week-task">
                  {{ task.title }}
                </div>
                <button v-if="day.isSelected" class="btn btn-sm btn-primary add-task-quick" @click="quickAddTask(day.fullDate)">
                  <i class="bi bi-plus"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Day View -->
          <div v-else-if="selectedView === 'day'" class="day-view">
            <div class="day-header">
              <h5 class="fw-bold">{{ selectedDateFormatted }}</h5>
              <p class="text-muted">{{ getDayTasks().length }} tasks scheduled</p>
            </div>
            <div class="day-timeline">
              <div v-for="hour in dayHours" :key="hour" class="time-slot">
                <div class="time-label">{{ formatHour(hour) }}</div>
                <div class="time-content">
                  <div v-for="task in getTasksForHour(hour)" :key="task.id" class="time-task" @click="editTask(task)">
                    <span class="task-title">{{ task.title }}</span>
                    <span class="task-time">{{ task.dueTime }}</span>
                  </div>
                  <button class="btn btn-sm btn-outline-primary add-task-time" @click="addTaskAtTime(hour)">
                    <i class="bi bi-plus"></i> Add task
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Progress & Tasks Section -->
      <div class="col-lg-4 mb-4">
        <!-- Progress Card -->
        <div class="modern-card p-4 mb-4">
          <h5 class="fw-bold mb-3">PROGRESS</h5>
          
          <!-- Circular Progress (dynamic) -->
          <div class="d-flex justify-content-center mb-3">
            <div class="progress-ring">
              <svg width="120" height="120">
                <circle cx="60" cy="60" r="50" stroke="#e5e7eb" stroke-width="8" fill="none"/>
                <circle 
                  cx="60" cy="60" r="50" 
                  stroke="#3b82f6" 
                  stroke-width="8" 
                  fill="none"
                  stroke-dasharray="314"
                  :stroke-dashoffset="ringDashOffset"
                  stroke-linecap="round"
                  transform="rotate(-90 60 60)"
                  :style="{ transition: 'stroke-dashoffset 0.6s ease' }"
                />
                <text x="60" y="65" text-anchor="middle" class="progress-text">{{ progressPercent }}%</text>
                <text x="60" y="80" text-anchor="middle" class="progress-label">Tasks Done</text>
              </svg>
            </div>
          </div>

          <div class="text-center mb-3">
            <h6 class="fw-bold">{{ filteredCompletedCount }} of {{ filteredTotalCount }} completed this {{ periodLabel }}</h6>
            <small class="text-muted" v-if="filteredTotalCount > 0">{{ remainingCount }} remaining</small>
          </div>

          <!-- How Are You Feeling -->
          <div class="text-center">
            <h6 class="fw-bold mb-2">HOW ARE YOU FEELING?</h6>
            <div class="d-flex justify-content-center gap-2">
              <span v-for="emotion in emotions" :key="emotion.emoji" 
                    class="emotion-selector" 
                    :class="{ active: selectedEmotion === emotion.emoji }"
                    @click="setEmotion(emotion.emoji)">
                {{ emotion.emoji }}
              </span>
            </div>
          </div>
          <div class="text-center mt-2">
            <button class="btn btn-sm btn-outline-primary" @click="ctaAddTask">
              <i class="bi bi-plus-lg me-1"></i>Add task for this {{ selectedView === 'week' ? 'week' : 'day' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Today's Tasks Section -->
    <div class="row">
      <div class="col-12">
        <div class="modern-card p-4">
          <div class="d-flex justify-content-between align-items-center mb-4">
            <h4 class="fw-bold mb-0">TODAY'S TASKS</h4>
            <button class="btn btn-primary" @click="showAddTask = true">
              <i class="bi bi-plus-lg me-2"></i>Add New Task
            </button>
          </div>

          <!-- Task List -->
          <div class="task-list">
            <div v-for="task in getDayTasks()" :key="task.id" class="task-item d-flex align-items-center justify-content-between p-3 mb-3" :class="{ 'completed-task-item': task.completed }">
              <div class="d-flex align-items-center">
                <div 
                  class="task-checkbox me-3"
                  :class="{ completed: task.completed }"
                  @click="toggleTask(task)"
                >
                  <i v-if="task.completed" class="bi bi-check-lg"></i>
                </div>
                <div>
                  <h6 class="mb-1 fw-bold task-title">
                    {{ task.title }}
                    <span v-if="task.completed" class="badge bg-success ms-2">
                      <i class="bi bi-check-circle me-1"></i>Completed
                    </span>
                  </h6>
                  <small class="text-muted">Due {{ task.dueTime }}</small>
                  <span v-if="task.priority" class="badge ms-2" :class="getPriorityBadgeClass(task.priority)">
                    {{ task.priority.toUpperCase() }}
                  </span>
                </div>
              </div>
              
              <div class="d-flex align-items-center gap-2">
                <button class="btn btn-sm btn-outline-primary" @click="editTask(task)" :disabled="task.completed">
                  <i class="bi bi-pencil"></i>
                </button>
                <button class="btn btn-sm btn-outline-danger" @click="deleteTask(task)">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Add New Task Button -->
          <button 
            class="btn w-100 add-task-btn" 
            style="background: #7dd3fc; color: #0c4a6e; border-radius: 15px; padding: 15px;"
            @click="showAddTask = true"
          >
            <i class="bi bi-plus-lg me-2"></i>Add New Task
          </button>
        </div>
      </div>
    </div>

    <!-- Add Task Modal -->
    <div v-if="showAddTask" class="modal-overlay" @click="showAddTask = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h5 class="fw-bold">Add New Task</h5>
          <button class="btn-close" @click="showAddTask = false">×</button>
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <label class="form-label">Task Title</label>
            <input v-model="newTask.title" type="text" class="form-control" placeholder="Enter task title">
          </div>
          <div class="mb-3">
            <label class="form-label">Due Date</label>
            <input v-model="newTask.dueDate" type="date" class="form-control">
          </div>
          <div class="mb-3">
            <label class="form-label">Due Time</label>
            <input v-model="newTask.dueTime" type="time" class="form-control">
          </div>
          <div class="mb-3">
            <label class="form-label">Priority</label>
            <select v-model="newTask.priority" class="form-select">
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          <div class="mb-3">
            <label class="form-label">Description (Optional)</label>
            <textarea v-model="newTask.description" class="form-control" rows="3" placeholder="Add task details..."></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showAddTask = false">Cancel</button>
          <button class="btn btn-primary" @click="addTask">Add Task</button>
        </div>
      </div>
    </div>

    <!-- Edit Task Modal -->
    <div v-if="showEditTask" class="modal-overlay" @click="showEditTask = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h5 class="fw-bold">Edit Task</h5>
          <button class="btn-close" @click="showEditTask = false">×</button>
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <label class="form-label">Task Title</label>
            <input v-model="editingTask.title" type="text" class="form-control" placeholder="Enter task title">
          </div>
          <div class="mb-3">
            <label class="form-label">Due Date</label>
            <input v-model="editingTask.dueDate" type="date" class="form-control">
          </div>
          <div class="mb-3">
            <label class="form-label">Due Time</label>
            <input v-model="editingTask.dueTime" type="time" class="form-control">
          </div>
          <div class="mb-3">
            <label class="form-label">Priority</label>
            <select v-model="editingTask.priority" class="form-select">
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          <div class="mb-3">
            <label class="form-label">Description (Optional)</label>
            <textarea v-model="editingTask.description" class="form-control" rows="3" placeholder="Add task details..."></textarea>
          </div>
          <div class="mb-3">
            <label class="form-label">Status</label>
            <select v-model="editingTask.completed" class="form-select">
              <option :value="false">Pending</option>
              <option :value="true">Completed</option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showEditTask = false">Cancel</button>
          <button class="btn btn-primary" @click="updateTask">Save Changes</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PlannerPage',
  data() {
    return {
      selectedView: 'month',
      selectedDate: new Date(),
      selectedEmotion: '😊',
      showAddTask: false,
      showEditTask: false,
      currentDate: new Date(),
      views: ['month', 'week', 'day'],
      dayHeaders: ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'],
      dayHours: Array.from({ length: 16 }, (_, i) => i + 6), // 6 AM to 10 PM
      emotions: [
        { emoji: '😞', label: 'Sad' },
        { emoji: '😐', label: 'Neutral' },
        { emoji: '😊', label: 'Happy' },
        { emoji: '👍', label: 'Great' }
      ],
      todaysTasks: [],
      newTask: {
        title: '',
        dueDate: new Date().toISOString().split('T')[0],
        dueTime: '',
        priority: 'medium',
        description: ''
      },
      editingTask: {
        id: null,
        title: '',
        dueDate: '',
        dueTime: '',
        priority: 'medium',
        description: '',
        completed: false
      }
    }
  },
  async mounted() {
    await this.fetchTasksForCurrentUser()
  },
  computed: {
    // Filtering based on selected view (day/week)
    filteredTasks() {
      if (!Array.isArray(this.todaysTasks)) return []
      if (this.selectedView === 'month') {
        const start = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1)
        const end = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 0)
        const inRange = (d) => d >= start && d <= end
        return this.todaysTasks.filter(t => {
          const d = new Date(`${(t.dueDate || '').slice(0, 10)}T00:00:00`)
          return inRange(d)
        })
      }
      if (this.selectedView === 'week') {
        const start = new Date(this.selectedDate)
        start.setDate(start.getDate() - start.getDay())
        const end = new Date(start)
        end.setDate(start.getDate() + 6)
        const inRange = (d) => d >= start && d <= end
        return this.todaysTasks.filter(t => {
          const d = new Date(`${(t.dueDate || '').slice(0, 10)}T00:00:00`)
          return inRange(d)
        })
      }
      // default: day (use local Y-M-D to match stored dueDate)
      const y = this.selectedDate.getFullYear()
      const m = String(this.selectedDate.getMonth() + 1).padStart(2, '0')
      const d = String(this.selectedDate.getDate()).padStart(2, '0')
      const dayStr = `${y}-${m}-${d}`
      return this.todaysTasks.filter(t => (t.dueDate || '').slice(0, 10) === dayStr)
    },
    filteredTotalCount() {
      return this.filteredTasks.length
    },
    filteredCompletedCount() {
      return this.filteredTasks.filter(t => t.completed).length
    },
    remainingCount() {
      return Math.max(0, this.filteredTotalCount - this.filteredCompletedCount)
    },
    progressPercent() {
      if (this.filteredTotalCount === 0) return 0
      return Math.round((this.filteredCompletedCount / this.filteredTotalCount) * 100)
    },
    ringDashOffset() {
      // circumference ~ 2 * pi * r where r=50 -> 314
      const circumference = 314
      return circumference - (circumference * this.progressPercent) / 100
    },
    periodLabel() {
      return this.selectedView === 'week' ? 'week' : (this.selectedView === 'month' ? 'month' : 'day')
    },
    currentPeriodTitle() {
      switch (this.selectedView) {
        case 'week':
          return this.getWeekTitle();
        case 'day':
          return this.selectedDate.toLocaleDateString('en-US', { 
            weekday: 'long', 
            month: 'long', 
            day: 'numeric',
            year: 'numeric'
          });
        default:
          return this.currentDate.toLocaleDateString('en-US', { 
            month: 'long', 
            year: 'numeric' 
          });
      }
    },
    calendarDates() {
      const dates = [];
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();
      
      // Get first day of month and how many days to show from previous month
      const firstDay = new Date(year, month, 1);
      const startDate = new Date(firstDay);
      startDate.setDate(startDate.getDate() - firstDay.getDay());
      
      // Generate 42 days (6 weeks)
      for (let i = 0; i < 42; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        
        const isCurrentMonth = date.getMonth() === month;
        const isToday = this.isSameDate(date, new Date());
        const isSelected = this.isSameDate(date, this.selectedDate);
        const hasTasks = this.hasTasksOnDate(date);
        const taskCount = this.getTasksForDate(date).length;
        
        dates.push({
          key: date.toISOString(),
          day: date.getDate(),
          date: date,
          isCurrentMonth,
          isToday,
          isSelected,
          hasTasks,
          taskCount
        });
      }
      
      return dates;
    },
    weekDays() {
      const startOfWeek = new Date(this.selectedDate);
      startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
      
      const days = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + i);
        
        days.push({
          key: date.toISOString(),
          name: date.toLocaleDateString('en-US', { weekday: 'short' }),
          date: date.getDate(),
          fullDate: date,
          isToday: this.isSameDate(date, new Date()),
          isSelected: this.isSameDate(date, this.selectedDate)
        });
      }
      
      return days;
    },
    selectedDateFormatted() {
      return this.selectedDate.toLocaleDateString('en-US', { 
        weekday: 'long', 
        month: 'long', 
        day: 'numeric' 
      });
    }
  },
  methods: {
    currentUser() {
      const raw = localStorage.getItem('currentUser')
      return raw ? JSON.parse(raw) : null
    },

    // Helper function to format date as YYYY-MM-DD in local timezone
    formatDateLocal(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    async fetchTasksForCurrentUser() {
      try {
        const user = this.currentUser()
        if (!user?.id) return
        const { getUserTasks } = await import('../services/taskService.js')
        const tasks = await getUserTasks(user.id)
        // Convert backend priority numbers to frontend strings
        const priorityToString = { 3: 'high', 2: 'medium', 1: 'low' }
        
        this.todaysTasks = (tasks || []).map(t => ({
          id: t.id,
          title: t.title,
          dueDate: (t.due_date || '').split('T')[0] || new Date().toISOString().split('T')[0],
          dueTime: (t.dueTime || '').replace('Due Today ', '').replace('Due ', ''),
          priority: priorityToString[t.priority] || 'medium',
          completed: !!t.completed,
          description: t.description || ''
        }))
      } catch (e) {
        // keep empty on failure
        console.error('Failed to load tasks', e)
      }
    },
    // View switching methods
    switchView(view) {
      this.selectedView = view;
    },
    
    // Navigation methods
    previousPeriod() {
      switch (this.selectedView) {
        case 'month':
          this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() - 1, 1);
          break;
        case 'week':
          this.selectedDate = new Date(this.selectedDate.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'day':
          this.selectedDate = new Date(this.selectedDate.getTime() - 24 * 60 * 60 * 1000);
          break;
      }
    },
    
    nextPeriod() {
      switch (this.selectedView) {
        case 'month':
          this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 1);
          break;
        case 'week':
          this.selectedDate = new Date(this.selectedDate.getTime() + 7 * 24 * 60 * 60 * 1000);
          break;
        case 'day':
          this.selectedDate = new Date(this.selectedDate.getTime() + 24 * 60 * 60 * 1000);
          break;
      }
    },
    
    // Date and task methods
    selectDate(dateObj) {
      // Prefer actual Date objects from our calendar/weekly structures
      if (dateObj && dateObj.fullDate instanceof Date) {
        this.selectedDate = dateObj.fullDate
      } else if (dateObj && dateObj.date instanceof Date) {
        this.selectedDate = dateObj.date
      } else if (dateObj instanceof Date) {
        this.selectedDate = dateObj
      } else if (dateObj && typeof dateObj.date === 'number') {
        // weekDays uses numeric 'date' for day-of-month; build a Date from current context
        const base = new Date(this.selectedDate)
        base.setDate(dateObj.date)
        this.selectedDate = base
      } else {
        // Fallback: keep current selection
        this.selectedDate = new Date(this.selectedDate)
      }
    },
    
    isSameDate(date1, date2) {
      const d1 = (date1 instanceof Date) ? date1 : new Date(date1)
      const d2 = (date2 instanceof Date) ? date2 : new Date(date2)
      if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime())) return false
      return d1.toDateString() === d2.toDateString()
    },
    
    hasTasksOnDate(date) {
      return this.getTasksForDate(date).length > 0;
    },
    
    getTasksForDate(date) {
      // Use local date string to avoid timezone issues
      const dateString = this.formatDateLocal(date);
      return this.todaysTasks.filter(task => task.dueDate === dateString);
    },
    
    getDayTasks() {
      return this.getTasksForDate(this.selectedDate);
    },
    
    getTasksForHour(hour) {
      const dayTasks = this.getDayTasks();
      return dayTasks.filter(task => {
        if (!task.dueTime) return false;
        const taskHour = parseInt(task.dueTime.split(':')[0]);
        return taskHour === hour;
      });
    },
    
    // Task management methods
    async toggleTask(task) {
      try {
        task.completed = !task.completed
        this.showTaskCompletionFeedback(task)
        // persist status change
        const { updateTaskStatus } = await import('../services/taskService.js')
        await updateTaskStatus(task.id, task.completed ? 'Completed' : 'Not started')
      } catch (e) {
        // revert on failure
        task.completed = !task.completed
      }
    },
    
    editTask(task) {
      if (task.completed) return; // Disable editing completed tasks
      
      this.editingTask = {
        id: task.id,
        title: task.title,
        dueDate: task.dueDate,
        dueTime: task.dueTime,
        priority: task.priority,
        description: task.description || '',
        completed: task.completed
      };
      this.showEditTask = true;
    },
    
    async updateTask() {
      if (!this.editingTask.title.trim()) return
      const taskIndex = this.todaysTasks.findIndex(t => t.id === this.editingTask.id)
      if (taskIndex === -1) return
      const updated = { ...this.editingTask }
      this.todaysTasks[taskIndex] = updated
      this.showEditTask = false
      this.showTaskUpdateFeedback()
      try {
        const { default: axios } = await import('axios')
        const API = process.env.VUE_APP_API_URL || 'http://127.0.0.1:5000'
        // Convert priority string to number for backend compatibility
        const priorityMap = { 'high': 3, 'medium': 2, 'low': 1 }
        const priorityNumber = priorityMap[updated.priority] || 2
        
        const payload = {
          title: updated.title,
          description: updated.description,
          due_date: `${updated.dueDate}T${(updated.dueTime || '00:00')}:00`,
          status: updated.completed ? 'Completed' : 'Not started',
          priority: priorityNumber
        }
        await axios.put(`${API}/api/tasks/update/${updated.id}`, payload)
      } catch (e) {
        console.error('Failed to update task:', e)
        this.showTaskSaveError('Failed to update task. Please try again.')
      }
    },
    
    async deleteTask(task) {
      if (!confirm('Are you sure you want to delete this task?')) return
      const index = this.todaysTasks.indexOf(task)
      if (index === -1) return
      this.todaysTasks.splice(index, 1)
      this.showTaskDeleteFeedback()
      try {
        const { default: axios } = await import('axios')
        const API = process.env.VUE_APP_API_URL || 'http://127.0.0.1:5000'
        await axios.delete(`${API}/api/tasks/delete/${task.id}`)
      } catch (e) {
        console.error('Failed to delete task from backend:', e)
        this.showTaskSaveError('Failed to delete task permanently. Please try again.')
      }
    },
    
    async addTask() {
      if (!this.newTask.title.trim()) return
      const user = this.currentUser()
      const assigned_to = user?.id
      const newLocal = {
        id: Date.now(),
        title: this.newTask.title,
        dueDate: this.newTask.dueDate,
        dueTime: this.newTask.dueTime,
        priority: this.newTask.priority,
        description: this.newTask.description,
        completed: false
      }
      this.todaysTasks.push(newLocal)
      this.newTask = { title: '', dueDate: new Date().toISOString().split('T')[0], dueTime: '', priority: 'medium', description: '' }
      this.showAddTask = false
      this.showTaskAddFeedback()
      try {
        const { default: axios } = await import('axios')
        const API = process.env.VUE_APP_API_URL || 'http://127.0.0.1:5000'
        
        // Convert priority string to number for backend compatibility
        const priorityMap = { 'high': 3, 'medium': 2, 'low': 1 }
        const priorityNumber = priorityMap[newLocal.priority] || 2
        
        const payload = {
          title: newLocal.title,
          description: newLocal.description,
          due_date: `${newLocal.dueDate}T${(newLocal.dueTime || '00:00')}:00`,
          priority: priorityNumber,
          assigned_to
        }
        const res = await axios.post(`${API}/api/tasks/create`, payload)
        const createdId = res?.data?.task_id
        if (createdId) {
          const idx = this.todaysTasks.findIndex(t => t.id === newLocal.id)
          if (idx > -1) this.todaysTasks[idx].id = createdId
          
          // Refresh dashboard if it's a student dashboard component
          this.notifyDashboardRefresh()
        }
      } catch (e) {
        console.error('Failed to save task to backend:', e)
        // Show error to user but keep local task for now
        this.showTaskSaveError(e.message || 'Failed to save task. Please try again.')
      }
    },
    
    quickAddTask(date) {
      // Use local date string to avoid timezone issues
      this.newTask.dueDate = this.formatDateLocal(date);
      this.showAddTask = true;
    },
    
    addTaskAtTime(hour) {
      // Use local date string to avoid timezone issues
      this.newTask.dueDate = this.formatDateLocal(this.selectedDate);
      this.newTask.dueTime = `${hour.toString().padStart(2, '0')}:00`;
      this.showAddTask = true;
    },
    ctaAddTask() {
      // Prefill dueDate for the current selection (day or week start)
      const pref = new Date(this.selectedDate)
      if (this.selectedView === 'week') {
        pref.setDate(pref.getDate() - pref.getDay())
      }
      this.newTask.dueDate = this.formatDateLocal(pref)
      this.newTask.dueTime = ''
      this.showAddTask = true
    },
    
    // Utility methods
    getPriorityBadgeClass(priority) {
      switch (priority) {
        case 'high': return 'badge-danger';
        case 'medium': return 'badge-warning';
        case 'low': return 'badge-info';
        default: return 'badge-secondary';
      }
    },
    
    formatHour(hour) {
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      return `${displayHour}:00 ${ampm}`;
    },
    
    getWeekTitle() {
      const start = new Date(this.selectedDate);
      start.setDate(start.getDate() - start.getDay());
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    },
    
    // Feedback methods
    showTaskCompletionFeedback(task) {
      const message = task.completed ? 'Task marked as completed! 🎉' : 'Task marked as pending.';
      this.showFeedback(message, task.completed ? 'success' : 'info');
    },
    
    showTaskAddFeedback() {
      this.showFeedback('Task added successfully! 📝', 'success');
    },
    
    showTaskUpdateFeedback() {
      this.showFeedback('Task updated successfully! ✏️', 'success');
    },
    
    showTaskDeleteFeedback() {
      this.showFeedback('Task deleted successfully! 🗑️', 'info');
    },
    
    showTaskSaveError(message) {
      this.showFeedback(message, 'error');
    },
    
    // Notify parent dashboard to refresh data
    notifyDashboardRefresh() {
      // Set a flag that the dashboard can check
      localStorage.setItem('tasksUpdated', Date.now().toString())
      
      // If we're in the student dashboard context, emit event
      this.$emit('tasks-updated')
    },
    
    showFeedback(message, type) {
      const alertClass = type === 'success' ? 'alert-success' : type === 'error' ? 'alert-danger' : 'alert-info';
      const alert = document.createElement('div');
      alert.className = `alert ${alertClass} alert-dismissible fade show position-fixed`;
      alert.style.cssText = 'top: 20px; right: 20px; z-index: 9999; min-width: 300px;';
      alert.innerHTML = `
        <strong>${message}</strong>
        <button type="button" class="btn-close" onclick="this.parentElement.remove()"></button>
      `;
      
      document.body.appendChild(alert);
      setTimeout(() => {
        if (alert.parentNode) {
          alert.parentNode.removeChild(alert);
        }
      }, 3000);
    },

    async setEmotion(emoji) {
      this.selectedEmotion = emoji
      try {
        // Persist as a feeling reflection (optional best-effort)
        const user = this.currentUser()
        if (!user?.id) return
        const { default: axios } = await import('axios')
        const API = process.env.VUE_APP_API_URL || 'http://127.0.0.1:5000'
        await axios.post(`${API}/api/feeling`, {
          emotion: this.mapEmojiToEmotion(emoji),
          content: 'Planner mood check',
          student_id: user.id
        })
      } catch (e) {
        // Ignore persistence failures
      }
    },

    mapEmojiToEmotion(emoji) {
      switch (emoji) {
        case '😞': return 'sad'
        case '😐': return 'content'
        case '😊': return 'happy'
        case '👍': return 'proud'
        default: return 'happy'
      }
    }
  }
}
</script>

<style scoped>
.view-btn {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  padding: 10px 24px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.view-btn:first-child {
  border-radius: 15px 0 0 15px;
}

.view-btn:last-child {
  border-radius: 0 15px 15px 0;
}

.view-btn.active {
  background: var(--role-primary);
  color: white;
  border-color: var(--role-primary);
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
  background: var(--border-color);
  border-radius: 10px;
  overflow: hidden;
}

.calendar-header {
  background: var(--bg-secondary);
  padding: 12px;
  text-align: center;
  font-weight: 600;
  font-size: 12px;
  color: var(--text-secondary);
}

.calendar-day {
  background: var(--bg-primary);
  padding: 12px 8px;
  min-height: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  color: var(--text-primary);
}

.calendar-day:hover {
  background: var(--bg-secondary);
}

.calendar-day.other-month {
  color: var(--text-muted);
  background: var(--bg-tertiary);
}

.calendar-day.today {
  background: var(--role-light);
  color: var(--role-primary);
  font-weight: bold;
}

.calendar-day.selected {
  background: var(--role-primary);
  color: white;
  border-radius: 8px;
}

.calendar-day.has-tasks .task-indicator {
  width: 6px;
  height: 6px;
  background: #ef4444;
  border-radius: 50%;
  margin-top: 4px;
}

.date-number {
  font-weight: 500;
}

.progress-ring {
  position: relative;
}

.progress-text {
  font-size: 18px;
  font-weight: bold;
  fill: var(--role-primary);
}

.progress-label {
  font-size: 12px;
  fill: var(--text-secondary);
}

.emotion-selector {
  font-size: 24px;
  padding: 8px;
  border-radius: 50%;
  background: var(--bg-secondary);
  transition: all 0.3s ease;
  cursor: pointer;
}

.emotion-selector:hover {
  transform: scale(1.2);
}

.emotion-selector.active {
  background: var(--role-primary);
  transform: scale(1.2);
}

.task-item {
  background: var(--bg-secondary);
  border-radius: 12px;
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;
}

.task-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.task-checkbox {
  width: 24px;
  height: 24px;
  border: 2px solid var(--border-color);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.task-checkbox.completed {
  background: #10b981;
  border-color: #10b981;
  color: white;
}

.add-task-btn {
  font-weight: 600;
  transition: all 0.3s ease;
}

.add-task-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: var(--bg-primary);
  border-radius: 15px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  color: var(--text-primary);
}

.modal-header {
  padding: 20px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-body {
  padding: 20px;
}

.modal-footer {
  padding: 20px;
  border-top: 1px solid var(--border-color);
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.btn-close {
  background: none;
  border: none;
  font-size: 24px;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0;
  margin-left: auto;
}

/* Week View Styles */
.week-view {
  background: var(--bg-primary);
  border-radius: 10px;
  overflow: hidden;
}

.week-header {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
}

.week-day-header {
  padding: 15px 10px;
  text-align: center;
  border-right: 1px solid var(--border-color);
}

.week-day-header:last-child {
  border-right: none;
}

.day-name {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 12px;
  margin-bottom: 5px;
}

.day-date {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-secondary);
}

.day-date.today {
  color: var(--role-primary);
  background: var(--role-light);
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.week-body {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  min-height: 400px;
}

.week-day-column {
  padding: 15px 10px;
  border-right: 1px solid var(--border-color);
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.week-day-column:hover {
  background: var(--bg-secondary);
}

.week-day-column:last-child {
  border-right: none;
}

.week-task {
  background: var(--role-light);
  color: var(--role-primary);
  padding: 5px 8px;
  border-radius: 5px;
  font-size: 12px;
  margin-bottom: 5px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.week-task:hover {
  background: var(--role-primary);
  color: white;
}

.add-task-quick {
  margin-top: 10px;
  font-size: 12px;
  padding: 5px 10px;
}

/* Day View Styles */
.day-view {
  background: var(--bg-primary);
  border-radius: 10px;
  overflow: hidden;
}

.day-header {
  padding: 20px;
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
  text-align: center;
}

.day-timeline {
  max-height: 500px;
  overflow-y: auto;
}

.time-slot {
  display: grid;
  grid-template-columns: 80px 1fr;
  border-bottom: 1px solid var(--border-color);
  min-height: 60px;
}

.time-label {
  padding: 15px 10px;
  background: var(--bg-secondary);
  border-right: 1px solid var(--border-color);
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
}

.time-content {
  padding: 10px;
  position: relative;
  min-height: 60px;
}

.time-task {
  background: var(--role-light);
  border: 1px solid var(--role-primary);
  border-radius: 5px;
  padding: 8px 10px;
  margin-bottom: 5px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.time-task:hover {
  background: var(--role-primary);
  color: white;
}

.task-title {
  font-weight: 600;
  display: block;
}

.task-time {
  font-size: 11px;
  opacity: 0.8;
}

.add-task-time {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 12px;
  padding: 5px 10px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.time-content:hover .add-task-time {
  opacity: 1;
}

/* Enhanced Task Styles */
.completed-task-item {
  background: var(--bg-tertiary) !important;
  border-color: #10b981 !important;
  opacity: 0.8;
}

.task-title {
  transition: all 0.3s ease;
}

.badge-danger {
  background-color: #ef4444 !important;
}

.badge-warning {
  background-color: #f59e0b !important;
}

.badge-info {
  background-color: #06b6d4 !important;
}

.task-count {
  position: absolute;
  top: 5px;
  right: 5px;
  background: var(--role-primary);
  color: white;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

/* Priority Badge Animations */
.badge {
  transition: all 0.3s ease;
}

.badge:hover {
  transform: scale(1.1);
}

/* Improved Calendar Day Styling */
.calendar-day.has-tasks {
  font-weight: 600;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .week-header,
  .week-body {
    grid-template-columns: repeat(7, 1fr);
    gap: 1px;
  }
  
  .week-day-header {
    padding: 10px 5px;
  }
  
  .day-name {
    font-size: 10px;
  }
  
  .day-date {
    font-size: 14px;
  }
  
  .time-slot {
    grid-template-columns: 60px 1fr;
  }
  
  .time-label {
    font-size: 10px;
    padding: 10px 5px;
  }
}
</style> 